<script>
import { Group, Line, RegularPolygon } from "svelte-konva";
import { viewportState } from "@app/lib/timeline/viewport.svelte.js";
import { playerState } from "@app/store/playerStore.svelte.js";
import { themeState } from "@app/store/themeStore.svelte.js";
import { getTimelinePalette } from "@app/lib/timeline/timelinePalette.js";

/**
 * Vertical playhead line + draggable triangle head.
 *
 * - Position is `playerState.time * pxPerSec - scrollX`, recomputed reactively.
 * - The triangle head is draggable horizontally: dragging scrubs the player.
 * - Viewport follow/auto-center behavior lives in Timeline.svelte, so this
 *   component only renders the playhead and handles scrub gestures.
 *
 * @typedef {Object} Props
 * @property {number} y         - y offset within parent layer (where line starts)
 * @property {number} height    - vertical length of the line
 * @property {string} [color]
 */

/** @type {Props} */
let { y = 0, height = 100, color } = $props();

let palette = $derived(getTimelinePalette(themeState.effective));
let playheadColor = $derived(color ?? palette.playhead);

const HEAD_RADIUS = 7;
const HEAD_Y_OFFSET = -2; // sit slightly above the line so the apex touches y

let dragging = $state(false);

let playheadX = $derived.by(() => {
    if (!viewportState.pxPerSec) return -1;
    return Math.round(playerState.time * viewportState.pxPerSec - viewportState.scrollX);
});

function handleHeadDragStart() {
    dragging = true;
}

function headDragBoundFunc(pos) {
    // Constrain to body row y position. x is free within the stage bounds.
    return { x: pos.x, y: y + HEAD_Y_OFFSET };
}

function handleHeadDragMove(e) {
    const pxPerSec = viewportState.pxPerSec;
    if (!pxPerSec) return;
    const node = e?.target;
    if (!node) return;
    const newX = node.x();
    const newTime = (newX + viewportState.scrollX) / pxPerSec;
    if (window.player && Number.isFinite(newTime)) {
        window.player.currentTime = Math.max(0, newTime);
    }
}

function handleHeadDragEnd(e) {
    dragging = false;
    handleHeadDragMove(e);
}
</script>

<Group listening={true}>
    <!-- The line itself does not need to listen; only the head should be hit. -->
    <Line
        points={[playheadX, y, playheadX, y + height]}
        stroke={playheadColor}
        strokeWidth={1.5}
        listening={false} />

    <RegularPolygon
        x={playheadX}
        y={y + HEAD_Y_OFFSET}
        sides={3}
        radius={HEAD_RADIUS}
        rotation={180}
        fill={playheadColor}
        draggable={true}
        dragBoundFunc={headDragBoundFunc}
        ondragstart={handleHeadDragStart}
        ondragmove={handleHeadDragMove}
        ondragend={handleHeadDragEnd} />
</Group>
